import React, { useState } from 'react';
import useTranslation from '../../hooks/useTranslation';
import TruckCard from './TruckCard';
import BottomSheet from './BottomSheet';
import MapLegend from './MapLegend';
import { useMediaQuery, TRACKER_PANEL_QUERY } from './useMediaQuery';
import { getDistanceFromLatLonInKm } from '../../utils/geo';
import { isVisibleTruck, hasLiveGps } from './truckStatus';
import './NearbyPanel.css';

/**
 * "In de buurt" - the nearest available trucks, sorted by distance.
 * Desktop/tablet: docked side panel. Mobile: bottom sheet with grip.
 *
 * Purely presentational: derives everything from the karren array that
 * TrackerView already polls, plus the userLocation it already tracks.
 * Deliberately does NOT call navigator.geolocation or touch the
 * 'geolocation' localStorage key - the call-flow branches on those.
 */
function NearbyPanel({ karren = [], userLocation = null, onSelectTruck = null, onShareLocation = null }) {
    const { t } = useTranslation();
    const isWide = useMediaQuery(TRACKER_PANEL_QUERY);
    const [expanded, setExpanded] = useState(false);

    // Show today's roster except not-scheduled trucks. The scoop-marker colour
    // in each row reflects the status (incl. "gepland"). Sorting: available
    // (driving) first by distance, then scheduled by planned time.
    const visible = karren.filter(isVisibleTruck);
    const rows = visible.map(kar => {
        let distanceKm = null;
        if (userLocation && userLocation.lat != null && userLocation.lng != null && kar.latitude != null && kar.longitude != null && hasLiveGps(kar)) {
            distanceKm = getDistanceFromLatLonInKm(
                parseFloat(userLocation.lat), parseFloat(userLocation.lng),
                parseFloat(kar.latitude), parseFloat(kar.longitude)
            );
        }
        return { kar, distanceKm };
    });

    const rank = (k) => (k.available ? 0 : k.status === 'SCHEDULED' ? 2 : 1);
    rows.sort((a, b) => {
        const ra = rank(a.kar), rb = rank(b.kar);
        if (ra !== rb) return ra - rb;
        if (ra === 2) {
            // both scheduled: by planned start time
            return String(a.kar.status_payload?.scheduled_at_datetime || '')
                .localeCompare(String(b.kar.status_payload?.scheduled_at_datetime || ''));
        }
        return (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity);
    });

    const availableCount = visible.filter(k => k.available).length;
    const title = t('tracker.trucks.title', 'IJskarren');
    const countLabel = t('tracker.trucks.count', '{available} van {total} beschikbaar')
        .replace('{available}', String(availableCount))
        .replace('{total}', String(visible.length));

    const list = (
        <div className="nearby-list">
            {/* Legend lives with the truck list on all sizes (not on the map). */}
            <MapLegend inline />
            {!userLocation && rows.length > 0 && (
                <div className="nearby-geo-hint">
                    <span>{t('tracker.nearby.geoHint', 'Deel je locatie om karren dichtbij eerst te zien.')}</span>
                    {onShareLocation && (
                        <button type="button" className="nearby-geo-btn" onClick={onShareLocation}>
                            {t('tracker.nearby.shareButton', 'Deel mijn locatie')}
                        </button>
                    )}
                </div>
            )}
            {rows.length === 0 ? (
                <div className="nearby-empty">
                    <span className="nearby-empty-script">{t('tracker.nearby.emptyScript', 'tot binnenkort!')}</span>
                    <p>{t('tracker.nearby.emptyText', 'Er rijden nu geen karren. Kom later terug - we rijden van de eerste lentezon tot midden oktober.')}</p>
                </div>
            ) : (
                rows.map(({ kar, distanceKm }) => (
                    <TruckCard
                        key={kar.id}
                        kar={kar}
                        distanceKm={distanceKm}
                        onSelect={onSelectTruck ? () => onSelectTruck("kar" + kar.id, kar.latitude, kar.longitude) : null}
                    />
                ))
            )}
        </div>
    );

    if (isWide) {
        return (
            <aside className="nearby-panel" aria-label={title}>
                <div className="nearby-panel-header">
                    <h2 className="nearby-panel-title">{title}</h2>
                    <span className="nearby-panel-count">{countLabel}</span>
                </div>
                {list}
            </aside>
        );
    }

    return (
        <BottomSheet
            peekLabel={countLabel}
            expanded={expanded}
            onToggle={setExpanded}
        >
            {list}
        </BottomSheet>
    );
}

export default NearbyPanel;
